/// What's New release log.
///
/// Bump the array with a new entry at the *top* (reverse-chronological
/// order — `WHATS_NEW[0]` is "latest") whenever you ship a noticeable
/// change to production. The `releaseId` of the top entry doubles as
/// the cache-buster: users who dismissed an older release get the
/// modal auto-opened the next time they load the site with a new
/// top entry.
///
/// Operational model:
///   - Edit this file in the same PR as the user-facing change.
///   - `releaseId` convention: `YYYY-MM-DD` (or `YYYY-MM-DD-suffix`
///     for multiple same-day deploys). Anything works as long as it
///     differs from previous entries.
///   - Keep `items[].text` to 1-3 sentences. Numbered list rendering
///     in `WhatsNewModal` handles the formatting; just write the
///     content.
///   - First-time visitors don't see this modal — they see the
///     WelcomeModal, then their `seen` flag for the latest release is
///     auto-set so they're not hit with two modals back-to-back. The
///     *next* release after their first visit will trigger normally.
///
/// Mirrors iOS's `FlatoutATC/Data/whats_new.json` but is web-specific
/// — the platforms ship different features, so reusing the iOS strings
/// would mislead web players (e.g. iOS has 3D terrain, web does not).

export interface WhatsNewItem {
  text: string
}

export interface WhatsNewRelease {
  /// Unique identifier for this release. Bumping this re-triggers the
  /// auto-open modal for every user who previously dismissed.
  releaseId: string
  /// Human-readable date shown in the modal header (e.g. "May 18, 2026").
  date: string
  /// Optional eyebrow/category text. Defaults to "Latest changes".
  title?: string
  /// Numbered list shown in the modal body. Order is preserved.
  items: readonly WhatsNewItem[]
}

/// Newest first. The first entry is the "latest" and drives auto-open.
export const WHATS_NEW: readonly WhatsNewRelease[] = [
  {
    releaseId: '2026-05-24',
    date: 'May 24, 2026',
    title: 'Graphical controls — heading dial + altitude tape',
    items: [
      {
        text: 'The heading input is now a compass rose with a draggable orange bug. Drag the bug around the rim, tap any point on the dial, or tap the center readout and type a heading on the keyboard. Confirm uses the shortest-arc turn; the explicit Turn Left / Turn Right buttons force that direction when you want the long way around (to delay an arrival, swing a helicopter around a town, etc).',
      },
      {
        text: 'The altitude input is now a vertical scrollable tape. Drag up to climb, drag down to descend, use the mouse scroll wheel for quick 100-foot steps, or tap the orange center readout to type a target altitude. Your aircraft\'s current altitude rides on the tape as a blue marker so you can see at a glance how far above or below the target it is.',
      },
      {
        text: 'The command panel is reorganized into collapsible sections: Vectors (heading + altitude), Speed, Direct-to / Hold, Approach. Vectors opens by default; tap any section header to open or close it. Cleared-to-land stays pinned at the bottom of the panel so the time-critical command is always one tap away.',
      },
    ],
  },
  {
    releaseId: '2026-05-23',
    date: 'May 23, 2026',
    title: 'New level — Anchorage / Ted Stevens',
    items: [
      {
        text: 'Anchorage (PANC) is now in the catalogue — a 24/7 cargo hub at the head of Cook Inlet, ringed by the Chugach Range to the east and the Talkeetna Mountains to the north. Three runways, FedEx / UPS / Atlas heavies on the Asia-to-Americas freight bridge, and medical helicopters between the Bowl and the bush. High terrain on every side.',
      },
    ],
  },
  {
    releaseId: '2026-05-21',
    date: 'May 21, 2026',
    title: 'New level — Lukla / Tenzing-Hillary',
    items: [
      {
        text: 'Lukla (VNLK) is now in the catalogue — the Himalayan gateway to Everest. A 527-metre runway carved into a ridge at 9,334 ft, with 8,000-foot mountain walls on every side and no go-around possible on final. Light aircraft and helicopters only. Widely considered the hardest airport in the world to fly into.',
      },
      {
        text: 'Helicopter service ceiling raised from 9,000 ft to 18,000 ft to match real-world utility helicopters (Bell 407, AS350 B3, EC130). Unlocks Lukla\'s Tengboche (12,664 ft) and Namche (11,286 ft) monastery helipads, and gives helicopters proper headroom at every mountain field.',
      },
    ],
  },
  {
    releaseId: '2026-05-20',
    date: 'May 20, 2026',
    title: 'Terrain readability + light aircraft ceiling',
    items: [
      {
        text: 'Terrain contours redesigned — elevation bands now use distinct hues (green for low rolling terrain, yellow-green for hills, yellow-tan for plateau, orange for slopes, red for peaks) instead of a brown-on-brown gradient. Higher elevations also get heavier line weights so mountain ridges read at a glance.',
      },
      {
        text: 'Elevation labels added on contours — bright cyan "1500ft" tags now appear along contour lines (multiple labels on long ridges), so you can read terrain heights directly instead of having to remember which band is which.',
      },
      {
        text: 'Light aircraft service ceiling raised from 12,000 to 16,000 ft. Survey and transition flights at mountain airports (Telluride, Aspen, Innsbruck) now have proper headroom above the 11,000 ft survey altitude — and turbocharged singles routinely operate at these altitudes anyway.',
      },
    ],
  },
  {
    releaseId: '2026-05-18-c',
    date: 'May 18, 2026',
    title: 'Recent improvements',
    items: [
      {
        text: 'Departures now show as small dots behind the runway threshold while they wait for clearance — you can see at a glance how many aircraft are queued for each runway. The dot promotes onto the runway automatically when the runway clears of arrivals on short final and any rolling departures.',
      },
      {
        text: 'Fixed a bug where vectoring an aircraft Direct-To a far-out navaid (like DRO at Telluride / Grand Canyon, or HEC at LAX) would score the aircraft as a missed exit before it ever reached the fix. Affects 14 levels — Telluride, Athens, Barcelona, Paris, Dubai, Innsbruck, Phoenix, LAX, Madrid, Narita, Rome, São Paulo, Tokyo, Washington Dulles.',
      },
      {
        text: 'Fixed a bug where a takeoff aircraft could appear on the runway while another aircraft was on short final to the same runway. Reported on Grand Canyon; was affecting every level.',
      },
      {
        text: 'Survey goals shipped across the catalogue — circle a landmark and exit via a navaid for light aircraft. Eiffel Tower, Sagrada Familia, Burj Khalifa, Camelback Mountain, Coronado Bay, and more.',
      },
      {
        text: 'Hospital helipads at 31 airports — vector helicopters to verified rooftop pads (Cedars-Sinai near LAX, Mass General near BOS, Lariboisière + Pitié-Salpêtrière near CDG, and twenty-plus more).',
      },
      {
        text: 'Map overlays added for major hubs — extra road and waterway data improves airspace situational awareness on LAX, ATL, IAD, ORD, Paris CDG and others.',
      },
      {
        text: 'Rating-recovery sweep across every shipping level. No more 2-3 hour timed marathons; Master rates tuned down at heavy hubs; every level opens with 1-4 aircraft already in the sector so you\'re not waiting on the first arrival.',
      },
      {
        text: '"Talk to the developer" board now lives at /talktothedeveloper — drop in questions, feature requests, or bug reports and get responses (with view + vote counts).',
      },
      {
        text: 'Cross-device progress: sign in with Google or email + password and your unlocks, scores and leaderboard rank follow you to any browser.',
      },
    ],
  },
]

export const LATEST_RELEASE_ID = WHATS_NEW[0]?.releaseId ?? ''

const SEEN_KEY_PREFIX = 'flatout.web.whatsNewSeen.'

/// True when the user has already dismissed the modal for
/// `LATEST_RELEASE_ID`. Used by HomeScreen to decide whether to
/// auto-open the modal on mount.
export function hasSeenLatestWhatsNew(): boolean {
  if (!LATEST_RELEASE_ID) return true
  try {
    return localStorage.getItem(SEEN_KEY_PREFIX + LATEST_RELEASE_ID) === '1'
  } catch {
    // localStorage may throw in private-mode Safari or when storage is
    // disabled — treat that as "seen" so we don't spam the modal.
    return true
  }
}

/// Record that the user has dismissed (or had auto-skipped) the modal
/// for the current latest release. Called from WhatsNewModal's dismiss
/// path AND from the WelcomeModal's first-time dismiss path (so a
/// fresh visitor doesn't get two modals in a row).
export function markLatestWhatsNewSeen(): void {
  if (!LATEST_RELEASE_ID) return
  try {
    localStorage.setItem(SEEN_KEY_PREFIX + LATEST_RELEASE_ID, '1')
  } catch {
    // Same private-mode swallow as above. The user will see the modal
    // again on their next visit; not catastrophic.
  }
}
